import React, { useMemo, useState } from "react";
import { Plus, Search, X } from "lucide-react";
import pricelist from "../../data/pricelist.json";
import { PRODUCTS } from "../site/ProductCollection";
import { PriceRow, suggested } from "./PriceRow";

const CATS = ["entry", "interior", "hardware", "garage", "windows"];
const WINDOWS = {
  label: "Windows & Aluminum Doors",
  notes: [
    "ViewLux aluminum systems are quoted per opening (size, glass, color, hardware) — add the product, then type the price.",
    "Use the photo + series code on the estimate so the customer sees exactly what they're buying.",
  ],
  items: PRODUCTS.map((p, i) => ({ id: `win-${i}`, ...p })),
};
const sectionFor = (cat) => (cat === "windows" ? WINDOWS : pricelist[cat]);
const abs = (p) => (p ? `${window.location.origin}${p}` : null);

const matches = (q, ...fields) => !q || fields.filter(Boolean).join(" ").toLowerCase().includes(q);

const Card = ({ id, image, title, subtitle, tags = [], children }) => (
  <article data-testid={`pb-card-${id}`} className="bg-white border border-[#c99658]/25 p-3 flex gap-3">
    {image && (
      <img src={image} alt={title} loading="lazy" className="w-[72px] h-[96px] object-contain bg-[#f7f2e8] border border-[#c99658]/20 shrink-0" />
    )}
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h5 className="font-cormorant text-xl leading-none text-[#0f2420]">{title}</h5>
        {subtitle && <span className="text-[11px] tracking-[0.15em] uppercase font-raleway text-[#7a5b2f]">{subtitle}</span>}
      </div>
      {tags.length > 0 && <p className="text-[11.5px] text-neutral-600 mt-0.5 leading-snug">{tags.filter(Boolean).join(" · ")}</p>}
      <div className="mt-2">{children}</div>
    </div>
  </article>
);

const WindowsList = ({ items, q, onAdd }) => items
  .filter((it) => matches(q, it.code, it.title, it.desc))
  .map((it) => (
    <Card key={it.id} id={it.id} image={it.img} title={it.title} subtitle={it.code} tags={[it.desc]}>
      <div className="flex items-center gap-2 pt-1">
        <span className="flex-1 text-[11px] italic text-neutral-500">Custom quote per opening</span>
        {onAdd && (
          <button type="button" data-testid={`add-${it.id}`} onClick={() => onAdd({
            description: `${it.title} · ${it.code} · size/glass/color: ___`,
            unit_price: 0, image_url: abs(it.img),
          })} className="h-7 px-3 flex items-center gap-1 bg-[#c99658] text-[#0f2420] text-[10px] tracking-[0.15em] uppercase font-raleway hover:bg-[#0f2420] hover:text-white transition-colors">
            <Plus size={12} /> Add
          </button>
        )}
      </div>
    </Card>
  ));

const EntryList = ({ items, q, onAdd }) => items
  .filter((it) => matches(q, it.design, it.finish, it.process, it.door_type))
  .map((it) => (
    <Card key={it.id} id={it.id} image={it.image} title={it.design} subtitle={it.finish} tags={[it.door_type, it.process, it.remarks]}>
      {it.prices.map((p, i) => (
        <PriceRow key={i} id={`${it.id}-${i}`} label={p.label} cost={p.cost} note={p.note}
          onAdd={onAdd && p.cost != null ? () => onAdd({
            description: `Entry Door ${it.design} · ${it.finish} · ${it.door_type} · ${p.label}`,
            unit_price: suggested(p.cost), image_url: abs(it.image),
          }) : undefined} />
      ))}
    </Card>
  ));

const InteriorList = ({ items, q, finish, onAdd }) => items
  .filter((it) => matches(q, it.design, it.door_type, it.process) && (!finish || it.prices.some((p) => p.finish === finish)))
  .map((it) => (
    <Card key={it.id} id={it.id} image={it.image} title={it.design} subtitle={it.door_type} tags={[it.process]}>
      {it.prices.filter((p) => !finish || p.finish === finish).map((p, i) => (
        <PriceRow key={i} id={`${it.id}-${i}`} label={`${p.finish} · ${p.size}`} cost={p.cost}
          onAdd={onAdd ? () => onAdd({
            description: `Interior ${it.door_type} ${it.design} · ${p.finish} · ${p.size} · ${it.process}`,
            unit_price: suggested(p.cost), image_url: abs(it.image),
          }) : undefined} />
      ))}
    </Card>
  ));

const HardwareList = ({ items, q, onAdd }) => items
  .filter((it) => matches(q, it.name, it.model, it.details, it.colors, it.door_types))
  .map((it) => (
    <Card key={it.id} id={it.id} image={it.image} title={it.name} subtitle={it.model} tags={[it.details, it.colors, it.door_types, it.remarks]}>
      <PriceRow id={it.id} label="Per set" cost={it.cost}
        onAdd={onAdd ? () => onAdd({
          description: `${it.name}${it.model ? ` ${it.model}` : ""} · ${it.colors}`,
          unit_price: suggested(it.cost), image_url: abs(it.image),
        }) : undefined} />
    </Card>
  ));

const GarageList = ({ items, q, onAdd }) => items
  .filter((it) => matches(q, it.name, it.notes))
  .map((it) => (
    <Card key={it.id} id={it.id} image={it.image} title={it.name} subtitle="No motor" tags={[it.notes]}>
      {it.colors_image && <img src={it.colors_image} alt="Color options" loading="lazy" className="h-8 object-contain mb-2" />}
      {it.sizes.map((s, i) => (
        <React.Fragment key={i}>
          <PriceRow id={`${it.id}-${i}`} label={s.size} cost={s.cost}
            onAdd={onAdd ? () => onAdd({ description: `Garage Door ${it.name} · ${s.size}`, unit_price: suggested(s.cost), image_url: abs(it.image) }) : undefined} />
          {s.insulated_cost != null && (
            <PriceRow id={`${it.id}-${i}-ins`} label={`${s.size} · Insulated`} cost={s.insulated_cost}
              onAdd={onAdd ? () => onAdd({ description: `Garage Door ${it.name} · ${s.size} · Insulated`, unit_price: suggested(s.insulated_cost), image_url: abs(it.image) }) : undefined} />
          )}
        </React.Fragment>
      ))}
    </Card>
  ));

export const PriceBook = ({ onAdd, onClose }) => {
  const [cat, setCat] = useState("entry");
  const [q, setQ] = useState("");
  const [finish, setFinish] = useState("");
  const query = q.trim().toLowerCase();
  const section = sectionFor(cat);
  const finishes = useMemo(() => [...new Set(pricelist.interior.items.flatMap((it) => it.prices.map((p) => p.finish)))], []);

  return (
    <div data-testid="price-book" className="flex flex-col h-full bg-cream">
      <div className="bg-[#0f2420] text-white px-5 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#e2b880]">Cost × 1.5 = suggested</div>
          <h3 className="font-cormorant italic text-2xl">Price Book</h3>
        </div>
        {onClose && <button data-testid="price-book-close" onClick={onClose} aria-label="Close price book" className="text-[#e2b880] hover:text-white"><X size={20} /></button>}
      </div>
      <div className="px-5 pt-3 bg-white border-b border-[#c99658]/25">
        <div className="flex gap-4 overflow-x-auto">
          {CATS.map((c) => (
            <button key={c} data-testid={`pb-tab-${c}`} onClick={() => { setCat(c); setFinish(""); }}
              className={`pb-2 whitespace-nowrap text-[11px] tracking-[0.25em] uppercase font-raleway border-b-2 ${cat === c ? "border-[#c99658] text-[#0f2420]" : "border-transparent text-neutral-500 hover:text-[#7a5b2f]"}`}>
              {sectionFor(c).label} <span className="text-neutral-400">{sectionFor(c).items.length}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="px-5 py-3 space-y-2 bg-white/60 border-b border-[#c99658]/15">
        <label className="flex items-center gap-2 border border-neutral-300 bg-white px-3 h-10">
          <Search size={15} className="text-neutral-400" />
          <input data-testid="pb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search design no., type, finish…" className="flex-1 text-sm outline-none bg-transparent" />
          {q && <button onClick={() => setQ("")} aria-label="Clear search" className="text-neutral-400 hover:text-[#0f2420]"><X size={14} /></button>}
        </label>
        {cat === "interior" && (
          <div className="flex gap-2 flex-wrap">
            {["", ...finishes].map((f) => (
              <button key={f} data-testid={`pb-finish-${f || "all"}`} onClick={() => setFinish(f)}
                className={`px-3 h-7 text-[10px] tracking-[0.15em] uppercase font-raleway border ${finish === f ? "bg-[#0f2420] text-white border-[#0f2420]" : "border-neutral-300 text-neutral-600 hover:border-[#7a5b2f]"}`}>
                {f || "All finishes"}
              </button>
            ))}
          </div>
        )}
        <ul className="text-[11px] text-neutral-600 leading-snug list-disc pl-4">
          {section.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>
      <div data-testid="pb-list" className="flex-1 overflow-y-auto p-4 space-y-2">
        {cat === "windows" && <WindowsList items={section.items} q={query} onAdd={onAdd} />}
        {cat === "entry" && <EntryList items={section.items} q={query} onAdd={onAdd} />}
        {cat === "interior" && <InteriorList items={section.items} q={query} finish={finish} onAdd={onAdd} />}
        {cat === "hardware" && <HardwareList items={section.items} q={query} onAdd={onAdd} />}
        {cat === "garage" && <GarageList items={section.items} q={query} onAdd={onAdd} />}
      </div>
    </div>
  );
};

export const PriceBookDrawer = ({ open, onAdd, onClose }) => {
  if (!open) return null;
  return (
    <div data-testid="price-book-drawer" className="fixed inset-0 z-[2100] flex justify-end overflow-hidden">
      <button aria-label="Close" onClick={onClose} className="flex-1 min-w-0 bg-[#0a1a17]/50 backdrop-blur-[2px]" />
      <div className="w-full max-w-2xl h-full shadow-2xl min-w-0">
        <PriceBook onAdd={onAdd} onClose={onClose} />
      </div>
    </div>
  );
};
